<script setup lang="ts">
import { compareEventDates, formatEventDate as formatPrecisionDate, isPlaced } from '../../client-db/src/event-date'

import { ref, watch, computed, onBeforeUnmount } from 'vue'
import { type DatabaseEvent, useEventStore } from '@/stores/event-store.ts'
import { useRouter } from 'vue-router'
import { localDB } from '@/migrate.ts'
import { eventFilesTable, eventsTable } from '../../client-db/src/db/schema.ts'
import { and, asc, desc, eq, inArray } from 'drizzle-orm'
import dayjs from 'dayjs'
import TopNav from '@/components/TopNav.vue'
import { FileStorageService } from '@/services/FileStorageService.ts'
import { IconSparkles, IconTrash } from '@tabler/icons-vue'

type TimelineEntry =
  | { kind: 'single'; event: DatabaseEvent }
  | { kind: 'group'; parent: DatabaseEvent; children: DatabaseEvent[] }

const eventStore = useEventStore()
const router = useRouter()
const data = ref<DatabaseEvent[]>([])
const eventPreviewImages = ref<Record<number, string>>({})
const eventImageCounts = ref<Record<number, number>>({})
const expandedGroups = ref<Record<number, boolean>>({})
const isMultiSelectMode = ref(false)
const multiSelectStep = ref<'select' | 'parent'>('select')
const selectedEventIds = ref<number[]>([])
const selectedParentEventId = ref<number | null>(null)
const groupingStatusMessage = ref<string | null>(null)
const groupingStatusType = ref<'error' | 'success' | null>(null)
const isApplyingGrouping = ref(false)
const isApplyingUngroup = ref(false)
const isDeletingSelection = ref(false)
const isAiRescueSearching = ref(false)
const suppressNextClick = ref(false)
const longPressTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const longPressEventId = ref<number | null>(null)
const longPressStartPoint = ref<{ x: number; y: number } | null>(null)

const fileStorage = new FileStorageService({
  adapter: 'hybrid',
  serverEndpoint: '/api',
  authToken: localStorage.getItem('yourKey') || '',
})

const displayEvents = computed(() => {
  return eventStore.hasActiveSearch ? eventStore.searchResults : data.value
})

const showUnplaced = ref(false)
const unplacedCount = computed(() => displayEvents.value.filter(event => !isPlaced(event)).length)
const placementEvents = computed(() => displayEvents.value.filter(event => isPlaced(event) !== showUnplaced.value))

function sortEventsByDateDesc(events: DatabaseEvent[]) {
  return [...events].sort(compareEventDates)
}

function sortEventsByDateAsc(events: DatabaseEvent[]) {
  return [...events].sort((a, b) => {
    if (!isPlaced(a) || !isPlaced(b)) return Number(isPlaced(b)) - Number(isPlaced(a)) || a.id - b.id
    return -compareEventDates(a, b)
  })
}

const timelineEntries = computed<TimelineEntry[]>(() => {
  const sortedEvents = sortEventsByDateDesc(placementEvents.value)
  const eventIds = new Set(sortedEvents.map((event) => event.id))
  const childrenByParent = new Map<number, DatabaseEvent[]>()
  const topLevelEvents: DatabaseEvent[] = []
  const orphanChildren: DatabaseEvent[] = []

  for (const event of sortedEvents) {
    if (event.parentEventId) {
      if (eventIds.has(event.parentEventId)) {
        const existingChildren = childrenByParent.get(event.parentEventId) || []
        existingChildren.push(event)
        childrenByParent.set(event.parentEventId, existingChildren)
      } else {
        orphanChildren.push(event)
      }
      continue
    }

    topLevelEvents.push(event)
  }

  const entries: TimelineEntry[] = []
  const entryByRootEventId = new Map<number, TimelineEntry>()

  for (const parent of topLevelEvents) {
    const children = sortEventsByDateAsc(childrenByParent.get(parent.id) || [])
    if (children.length > 0) {
      const entry: TimelineEntry = { kind: 'group', parent, children }
      entries.push(entry)
      entryByRootEventId.set(parent.id, entry)
    } else {
      const entry: TimelineEntry = { kind: 'single', event: parent }
      entries.push(entry)
      entryByRootEventId.set(parent.id, entry)
    }
  }

  for (const orphan of orphanChildren) {
    const entry: TimelineEntry = { kind: 'single', event: orphan }
    entries.push(entry)
    entryByRootEventId.set(orphan.id, entry)
  }

  const rootEvents = entries.map((entry) => (entry.kind === 'single' ? entry.event : entry.parent))
  const orderedRootEvents = sortEventsByDateDesc(rootEvents)

  return orderedRootEvents
    .map((event) => entryByRootEventId.get(event.id))
    .filter((entry): entry is TimelineEntry => Boolean(entry))
})

const visibleEventIds = computed(() => {
  return timelineEntries.value.flatMap((entry) =>
    entry.kind === 'single'
      ? [entry.event.id]
      : [entry.parent.id, ...entry.children.map((child) => child.id)],
  )
})

const canApplyGrouping = computed(() => {
  if (isApplyingGrouping.value || isApplyingUngroup.value || isDeletingSelection.value) return false
  if (selectedEventIds.value.length < 2 || !selectedParentEventId.value) return false
  return selectedEventIds.value.includes(selectedParentEventId.value)
})

const canUngroupSelection = computed(() => {
  if (isApplyingGrouping.value || isApplyingUngroup.value || isDeletingSelection.value) return false
  return selectedEventIds.value.length > 0
})

const selectedParentEvent = computed(() => {
  if (!selectedParentEventId.value) return null
  return displayEvents.value.find((event) => event.id === selectedParentEventId.value) || null
})

const eventsForPreviewLoading = computed(() => {
  return timelineEntries.value.flatMap((entry) => {
    if (entry.kind === 'single') return [entry.event]
    return [entry.parent, ...entry.children]
  })
})

watch(
  timelineEntries,
  (entries) => {
    const nextExpanded = { ...expandedGroups.value }

    for (const entry of entries) {
      if (entry.kind !== 'group' || !entry.parent.id) continue
      if (typeof nextExpanded[entry.parent.id] === 'undefined') {
        nextExpanded[entry.parent.id] = true
      }
    }

    expandedGroups.value = nextExpanded
  },
  { immediate: true },
)

function formatEventDateRange(event: DatabaseEvent) {
  return formatPrecisionDate(event)
}

function isGroupExpanded(parentId: number | null) {
  if (!parentId) return true
  return expandedGroups.value[parentId] ?? true
}

function toggleGroup(parentId: number | null) {
  if (!parentId) return
  expandedGroups.value[parentId] = !isGroupExpanded(parentId)
}

function handleToggleGroupClick(event: MouseEvent, parentId: number | null) {
  toggleGroup(parentId)
  const target = event.currentTarget
  if (target instanceof HTMLButtonElement) {
    target.blur()
  }
}

function clearGroupingStatus() {
  groupingStatusMessage.value = null
  groupingStatusType.value = null
}

function showGroupingError(message: string) {
  groupingStatusMessage.value = message
  groupingStatusType.value = 'error'
}

function showGroupingSuccess(message: string) {
  groupingStatusMessage.value = message
  groupingStatusType.value = 'success'
}

function enterMultiSelectMode(initialEventId: number | null = null) {
  isMultiSelectMode.value = true
  multiSelectStep.value = 'select'
  selectedEventIds.value = initialEventId ? [initialEventId] : []
  selectedParentEventId.value = null
  clearGroupingStatus()
}

function cancelMultiSelectMode() {
  isMultiSelectMode.value = false
  multiSelectStep.value = 'select'
  selectedEventIds.value = []
  selectedParentEventId.value = null
  clearGroupingStatus()
  clearLongPressTracking()
}

function isEventSelected(eventId: number | null) {
  if (!eventId) return false
  return selectedEventIds.value.includes(eventId)
}

function toggleEventSelection(eventId: number | null) {
  if (!isMultiSelectMode.value || !eventId) return

  if (isEventSelected(eventId)) {
    selectedEventIds.value = selectedEventIds.value.filter((id) => id !== eventId)
    if (selectedParentEventId.value === eventId) {
      selectedParentEventId.value = null
    }
  } else {
    selectedEventIds.value = [...selectedEventIds.value, eventId]
  }

  clearGroupingStatus()
}

function chooseSelectedEventAsParent(eventId: number | null) {
  if (!isMultiSelectMode.value || multiSelectStep.value !== 'parent' || !eventId) return
  if (!isEventSelected(eventId)) {
    selectedEventIds.value = [...selectedEventIds.value, eventId]
  }
  selectedParentEventId.value = eventId
  clearGroupingStatus()
}

function handleTimelineIconClick(event: MouseEvent, eventId: number | null) {
  event.stopPropagation()
  if (multiSelectStep.value === 'select') {
    toggleEventSelection(eventId)
    return
  }
  chooseSelectedEventAsParent(eventId)
}

function handleEventItemClick(event: DatabaseEvent) {
  if (suppressNextClick.value) {
    suppressNextClick.value = false
    return
  }

  if (isMultiSelectMode.value) {
    if (multiSelectStep.value === 'select') {
      toggleEventSelection(event.id)
    } else {
      chooseSelectedEventAsParent(event.id)
    }
    return
  }
  handleEventClick(event)
}

function clearLongPressTracking() {
  if (longPressTimeout.value) {
    clearTimeout(longPressTimeout.value)
    longPressTimeout.value = null
  }
  longPressEventId.value = null
  longPressStartPoint.value = null
}

function handleEventPointerDown(pointerEvent: PointerEvent, eventId: number | null) {
  if (isMultiSelectMode.value || !eventId) return
  if (pointerEvent.pointerType === 'mouse' && pointerEvent.button !== 0) return

  clearLongPressTracking()
  longPressEventId.value = eventId
  longPressStartPoint.value = { x: pointerEvent.clientX, y: pointerEvent.clientY }

  longPressTimeout.value = setTimeout(() => {
    if (longPressEventId.value !== eventId) return
    enterMultiSelectMode(eventId)
    suppressNextClick.value = true
    clearLongPressTracking()
  }, 420)
}

function handleEventPointerMove(pointerEvent: PointerEvent) {
  if (!longPressStartPoint.value || !longPressTimeout.value) return

  const deltaX = Math.abs(pointerEvent.clientX - longPressStartPoint.value.x)
  const deltaY = Math.abs(pointerEvent.clientY - longPressStartPoint.value.y)
  if (deltaX > 10 || deltaY > 10) {
    clearLongPressTracking()
  }
}

function handleEventPointerUp() {
  clearLongPressTracking()
}

function moveToChooseParentStep() {
  if (selectedEventIds.value.length < 2) {
    showGroupingError('Select at least two events before choosing a parent.')
    return
  }
  multiSelectStep.value = 'parent'
  if (
    !selectedParentEventId.value ||
    !selectedEventIds.value.includes(selectedParentEventId.value)
  ) {
    selectedParentEventId.value = selectedEventIds.value[0] ?? null
  }
  clearGroupingStatus()
}

function moveToSelectStep() {
  multiSelectStep.value = 'select'
  clearGroupingStatus()
}

async function getEventsThatCannotBeChildren(eventIds: number[]) {
  if (eventIds.length === 0) return []

  const childRows = await localDB
    .select({ parentEventId: eventsTable.parentEventId })
    .from(eventsTable)
    .where(inArray(eventsTable.parentEventId, eventIds))

  const blockedIds = new Set<number>()
  for (const row of childRows) {
    if (typeof row.parentEventId === 'number') {
      blockedIds.add(row.parentEventId)
    }
  }

  return [...blockedIds]
}

async function applyGroupingChanges() {
  if (!canApplyGrouping.value) {
    showGroupingError(
      'Select at least two events and choose which selected event should be the parent.',
    )
    return
  }

  const parentId = selectedParentEventId.value
  if (!parentId) {
    showGroupingError('Choose a parent event from the selected events.')
    return
  }
  const childIds = selectedEventIds.value.filter((eventId) => eventId !== parentId)

  if (childIds.length === 0) {
    showGroupingError('Select at least one child event to group under the parent event.')
    return
  }

  const blockedChildren = await getEventsThatCannotBeChildren(childIds)
  if (blockedChildren.length > 0) {
    showGroupingError(
      `${blockedChildren.length} selected event${blockedChildren.length > 1 ? 's' : ''} already contain child events and cannot be moved under a parent.`,
    )
    return
  }

  isApplyingGrouping.value = true
  clearGroupingStatus()

  try {
    await localDB
      .update(eventsTable)
      .set({ parentEventId: null })
      .where(eq(eventsTable.id, parentId))

    await localDB
      .update(eventsTable)
      .set({ parentEventId: parentId })
      .where(inArray(eventsTable.id, childIds))

    await loadEvents()
    selectedEventIds.value = []
    selectedParentEventId.value = null
    multiSelectStep.value = 'select'
    isMultiSelectMode.value = false
    showGroupingSuccess('Grouping updated.')
  } catch (error) {
    console.error('Failed to apply grouping changes:', error)
    showGroupingError('Failed to update group. Please try again.')
  } finally {
    isApplyingGrouping.value = false
  }
}

async function ungroupSelectedEvents() {
  if (!canUngroupSelection.value) {
    showGroupingError('Select one or more events to ungroup.')
    return
  }

  isApplyingUngroup.value = true
  clearGroupingStatus()

  try {
    await localDB
      .update(eventsTable)
      .set({ parentEventId: null })
      .where(inArray(eventsTable.id, selectedEventIds.value))

    await loadEvents()
    selectedEventIds.value = []
    selectedParentEventId.value = null
    multiSelectStep.value = 'select'
    isMultiSelectMode.value = false
    showGroupingSuccess('Selected events were ungrouped.')
  } catch (error) {
    console.error('Failed to ungroup selected events:', error)
    showGroupingError('Failed to ungroup selected events. Please try again.')
  } finally {
    isApplyingUngroup.value = false
  }
}

async function deleteSelectedEvents() {
  if (selectedEventIds.value.length === 0) {
    showGroupingError('Select one or more events to delete.')
    return
  }

  const selectedCount = selectedEventIds.value.length
  const confirmed = window.confirm(
    `Delete ${selectedCount} selected event${selectedCount > 1 ? 's' : ''}? This cannot be undone.`,
  )
  if (!confirmed) return

  isDeletingSelection.value = true
  clearGroupingStatus()

  try {
    await localDB
      .update(eventsTable)
      .set({ parentEventId: null })
      .where(inArray(eventsTable.parentEventId, selectedEventIds.value))

    await localDB.delete(eventsTable).where(inArray(eventsTable.id, selectedEventIds.value))

    await loadEvents()
    selectedEventIds.value = []
    selectedParentEventId.value = null
    multiSelectStep.value = 'select'
    isMultiSelectMode.value = false
  } catch (error) {
    console.error('Failed to delete selected events:', error)
    showGroupingError('Failed to delete selected events. Please try again.')
  } finally {
    isDeletingSelection.value = false
  }
}

async function loadEventPreviewImages(events: DatabaseEvent[]) {
  const eventIds = events
    .map((event) => event.id)
    .filter((id): id is number => typeof id === 'number')

  if (eventIds.length === 0) {
    eventPreviewImages.value = {}
    eventImageCounts.value = {}
    return
  }

  try {
    const thumbnailFiles = await localDB
      .select()
      .from(eventFilesTable)
      .where(
        and(inArray(eventFilesTable.eventId, eventIds), eq(eventFilesTable.fileType, 'THUMBNAIL')),
      )
      .orderBy(asc(eventFilesTable.eventId), asc(eventFilesTable.id))

    const imageFiles = await localDB
      .select()
      .from(eventFilesTable)
      .where(and(inArray(eventFilesTable.eventId, eventIds), eq(eventFilesTable.fileType, 'IMAGE')))
      .orderBy(asc(eventFilesTable.eventId), asc(eventFilesTable.id))

    const firstStorageIdByEvent: Record<number, string> = {}
    const imageCountsByEvent: Record<number, number> = {}

    for (const file of thumbnailFiles) {
      if (!file.eventId || !file.storageId || firstStorageIdByEvent[file.eventId]) continue
      firstStorageIdByEvent[file.eventId] = file.storageId
    }

    for (const file of imageFiles) {
      if (!file.eventId) continue
      imageCountsByEvent[file.eventId] = (imageCountsByEvent[file.eventId] || 0) + 1
      if (!file.storageId || firstStorageIdByEvent[file.eventId]) continue
      firstStorageIdByEvent[file.eventId] = file.storageId
    }

    const entries = await Promise.all(
      Object.entries(firstStorageIdByEvent).map(async ([eventId, storageId]) => {
        try {
          const url = await fileStorage.getFileUrl(storageId)
          return [Number(eventId), url] as const
        } catch (error) {
          console.error(`Failed to load preview image for event ${eventId}:`, error)
          return [Number(eventId), ''] as const
        }
      }),
    )

    eventPreviewImages.value = Object.fromEntries(entries.filter(([, url]) => url))
    eventImageCounts.value = imageCountsByEvent
  } catch (error) {
    console.error('Failed to load event preview images:', error)
    eventPreviewImages.value = {}
    eventImageCounts.value = {}
  }
}

function getEventPreviewImage(eventId: number) {
  return eventPreviewImages.value[eventId] || ''
}

function getEventImageCount(eventId: number) {
  return eventImageCounts.value[eventId] || 0
}

async function loadEvents() {
  if (eventStore.hasActiveSearch) {
    data.value = await eventStore.searchEvents(eventStore.searchQuery)
  } else {
    data.value = await localDB.select().from(eventsTable).orderBy(desc(eventsTable.date))
  }
}

async function tryAiSearchRescue() {
  const query = eventStore.searchQuery.trim()
  if (!query || isAiRescueSearching.value) return

  isAiRescueSearching.value = true
  try {
    await eventStore.parseSearchIntentWithAi(query)
    await loadEvents()
  } catch (error) {
    console.error('AI rescue search failed:', error)
    await eventStore.searchEvents(query)
    await loadEvents()
  } finally {
    isAiRescueSearching.value = false
  }
}

const handleEventClick = (event: any) => {
  eventStore.loadExistingEvent(event)
  router.push(`/event/${event.id}`)
}

watch(
  () => [eventStore.searchQuery, eventStore.searchFilterSignature] as const,
  async () => {
    await loadEvents()
  },
)

watch(
  eventsForPreviewLoading,
  async (events) => {
    await loadEventPreviewImages(events)
  },
  { immediate: true },
)

watch(
  visibleEventIds,
  (eventIds) => {
    const eventIdSet = new Set(eventIds)
    selectedEventIds.value = selectedEventIds.value.filter((eventId) => eventIdSet.has(eventId))
    if (selectedParentEventId.value && !eventIdSet.has(selectedParentEventId.value)) {
      selectedParentEventId.value = null
    }
  },
  { immediate: true },
)

watch(
  () => eventStore.showEvent,
  (newValue) => {
    if (!newValue) {
      if (router.currentRoute.value.name === 'event') {
        router.push('/')
      }
      loadEvents()
    }
  },
)

onBeforeUnmount(() => {
  clearLongPressTracking()
})

loadEvents()
</script>

<template>
  <main class="text-base-content">
    <div v-if="!eventStore.showEvent" class="sticky top-0 z-10 bg-base-100 py-1">
      <TopNav />
      <div v-if="isMultiSelectMode" class="px-2 pb-2">
        <div class="rounded-lg border border-base-300 bg-base-200/40 px-3 py-2">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm">
              Selected:
              <strong>{{ selectedEventIds.length }}</strong>
            </span>
            <span v-if="multiSelectStep === 'parent'" class="text-sm opacity-80">
              Tap one selected event to mark it as parent.
            </span>
            <span v-if="multiSelectStep === 'parent'" class="text-sm">
              Parent:
              <strong>{{ selectedParentEvent?.title || 'Untitled event' }}</strong>
            </span>
            <template v-if="multiSelectStep === 'select'">
              <button
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="
                  selectedEventIds.length < 2 ||
                  isApplyingGrouping ||
                  isApplyingUngroup ||
                  isDeletingSelection
                "
                @click="moveToChooseParentStep"
              >
                Group
              </button>
              <button
                type="button"
                class="btn btn-sm"
                :disabled="!canUngroupSelection"
                @click="ungroupSelectedEvents"
              >
                Ungroup Selected
              </button>
              <button
                type="button"
                class="btn btn-sm btn-error btn-outline gap-1"
                :disabled="selectedEventIds.length === 0 || isDeletingSelection"
                @click="deleteSelectedEvents"
              >
                <IconTrash :size="16" />
                Delete
              </button>
              <button
                type="button"
                class="btn btn-sm btn-ghost ml-auto"
                @click="cancelMultiSelectMode"
              >
                Cancel
              </button>
            </template>
            <template v-else>
              <button type="button" class="btn btn-sm" @click="moveToSelectStep">Back</button>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="!canApplyGrouping"
                @click="applyGroupingChanges"
              >
                Apply Group
              </button>
              <button
                type="button"
                class="btn btn-sm btn-ghost ml-auto"
                @click="cancelMultiSelectMode"
              >
                Cancel
              </button>
            </template>
          </div>
          <div
            v-if="groupingStatusMessage"
            class="mt-2 text-sm"
            :class="groupingStatusType === 'error' ? 'text-error' : 'text-success'"
          >
            {{ groupingStatusMessage }}
          </div>
        </div>
      </div>
    </div>

    <div class="px-4 py-2">
      <button type="button" class="btn btn-ghost btn-sm" @click="showUnplaced = !showUnplaced">
        {{ showUnplaced ? 'Back to timeline' : `Unplaced Memories (${unplacedCount})` }}
      </button>
      <p v-if="showUnplaced" class="text-sm opacity-70 mt-2">These memories are saved. Add a date whenever you remember.</p>
      <p v-if="placementEvents.length === 0" class="text-sm opacity-70 mt-2">{{ showUnplaced ? 'No unplaced memories.' : 'No dated memories in this view.' }}</p>
    </div>
    <ul
      v-if="displayEvents && displayEvents.length > 0"
      class="timeline timeline-snap-icon max-md:timeline-compact timeline-vertical p-2"
    >
      <li
        v-for="entry in timelineEntries"
        :key="entry.kind === 'single' ? `single-${entry.event.id}` : `group-${entry.parent.id}`"
        class="hover:bg-base-200"
      >
        <hr class="bg-brand-500" />
        <div class="timeline-middle">
          <button
            type="button"
            class="timeline-select-btn"
            :class="{
              'timeline-select-enabled': isMultiSelectMode,
              'timeline-select-active':
                entry.kind === 'single'
                  ? isEventSelected(entry.event.id)
                  : isEventSelected(entry.parent.id),
              'timeline-parent-active':
                isMultiSelectMode &&
                multiSelectStep === 'parent' &&
                (entry.kind === 'single'
                  ? entry.event.id === selectedParentEventId
                  : entry.parent.id === selectedParentEventId),
            }"
            :aria-label="
              isMultiSelectMode && multiSelectStep === 'parent'
                ? 'Choose selected event as parent'
                : isMultiSelectMode
                  ? 'Toggle event selection'
                  : 'Event marker'
            "
            @click="
              handleTimelineIconClick(
                $event,
                entry.kind === 'single' ? entry.event.id : entry.parent.id,
              )
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="icon icon-tabler icons-tabler-outline icon-tabler-circle text-brand-600"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
              <circle
                v-if="
                  entry.kind === 'single'
                    ? isEventSelected(entry.event.id)
                    : isEventSelected(entry.parent.id)
                "
                cx="12"
                cy="12"
                r="4"
                fill="currentColor"
                stroke="none"
              />
            </svg>
          </button>
        </div>

        <template v-if="entry.kind === 'single'">
          <div
            class="timeline-start mt-1.5 mb-4 md:text-end text-base-content flex flex-col"
            :class="isMultiSelectMode ? 'cursor-pointer' : ''"
            @pointerdown="handleEventPointerDown($event, entry.event.id)"
            @pointermove="handleEventPointerMove"
            @pointerup="handleEventPointerUp"
            @pointercancel="handleEventPointerUp"
            @click="handleEventItemClick(entry.event)"
          >
            <time class="font-light italic">{{
              formatPrecisionDate(entry.event)
            }}</time>
            <div class="text-lg font-semibold">{{ entry.event.title }}</div>
            <div
              v-if="
                isMultiSelectMode &&
                multiSelectStep === 'parent' &&
                entry.event.id === selectedParentEventId
              "
              class="mb-1"
            >
              <span class="badge badge-primary badge-sm">Parent</span>
            </div>
            <div class="py-2 px-1 rounded-lg bg-base-200 text-start">
              <div
                v-if="entry.event.id && getEventPreviewImage(entry.event.id)"
                class="relative float-left mr-3 mb-2 w-40 h-40 max-w-full"
              >
                <img
                  :src="getEventPreviewImage(entry.event.id)"
                  :alt="`Preview for ${entry.event.title || 'event'}`"
                  class="w-full h-full rounded-lg object-cover"
                />
                <span
                  v-if="getEventImageCount(entry.event.id) > 1"
                  class="badge badge-neutral badge-sm absolute bottom-2 right-2 bg-opacity-50"
                >
                  + {{ getEventImageCount(entry.event.id) - 1 }}
                </span>
              </div>
              <div class="whitespace-pre-wrap">
                {{ entry.event.description }}
              </div>
              <div class="clear-both"></div>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            class="timeline-start mt-1.5 mb-4 md:text-end text-base-content flex flex-col"
            :class="isMultiSelectMode ? 'cursor-pointer' : ''"
            @pointerdown="handleEventPointerDown($event, entry.parent.id)"
            @pointermove="handleEventPointerMove"
            @pointerup="handleEventPointerUp"
            @pointercancel="handleEventPointerUp"
            @click="handleEventItemClick(entry.parent)"
          >
            <div class="font-light italic">{{ formatEventDateRange(entry.parent) }}</div>
            <div class="text-lg font-semibold">{{ entry.parent.title }}</div>
            <div class="py-2 px-3 rounded-lg bg-base-200 text-start">
              <div class="flex items-stretch gap-3 cursor-pointer">
                <div
                  v-if="entry.parent.id && getEventPreviewImage(entry.parent.id)"
                  class="relative w-20 h-20 shrink-0"
                >
                  <img
                    :src="getEventPreviewImage(entry.parent.id)"
                    :alt="`Preview for ${entry.parent.title || 'event group'}`"
                    class="w-full h-full rounded-lg object-cover"
                  />
                </div>
                <div class="grow">
                  <div
                    v-if="
                      isMultiSelectMode &&
                      multiSelectStep === 'parent' &&
                      entry.parent.id === selectedParentEventId
                    "
                    class="mb-1"
                  >
                    <span class="badge badge-primary badge-sm">Parent</span>
                  </div>
                  <div class="whitespace-pre-wrap text-sm opacity-80">
                    {{ entry.parent.description }}
                  </div>
                </div>
                <button
                  class="group-toggle-btn self-stretch h-auto min-h-0 px-3 flex items-center justify-center"
                  type="button"
                  @pointerdown.stop
                  @click.stop="handleToggleGroupClick($event, entry.parent.id)"
                  :aria-label="isGroupExpanded(entry.parent.id) ? 'Collapse group' : 'Expand group'"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    :class="[
                      'transition-transform',
                      isGroupExpanded(entry.parent.id) ? 'rotate-180' : '',
                    ]"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
              </div>

              <Transition name="child-group">
                <ul
                  v-if="isGroupExpanded(entry.parent.id)"
                  class="mt-2 timeline timeline-vertical timeline-compact color-iris-500 child-timeline"
                >
                  <li v-for="(child, index) in entry.children" :key="`child-${child.id}`">
                    <hr v-if="index !== 0" class="bg-current" />
                    <div class="timeline-middle">
                      <button
                        type="button"
                        class="timeline-select-btn timeline-select-btn-sm"
                        :class="{
                          'timeline-select-enabled': isMultiSelectMode,
                          'timeline-select-active': isEventSelected(child.id),
                          'timeline-parent-active':
                            isMultiSelectMode &&
                            multiSelectStep === 'parent' &&
                            child.id === selectedParentEventId,
                        }"
                        :aria-label="
                          isMultiSelectMode && multiSelectStep === 'parent'
                            ? 'Choose selected child event as parent'
                            : isMultiSelectMode
                              ? 'Toggle child event selection'
                              : 'Child event marker'
                        "
                        @click="handleTimelineIconClick($event, child.id)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="icon icon-tabler icons-tabler-outline icon-tabler-circle text-current"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                          <circle
                            v-if="isEventSelected(child.id)"
                            cx="12"
                            cy="12"
                            r="4"
                            fill="currentColor"
                            stroke="none"
                          />
                        </svg>
                      </button>
                    </div>
                    <div
                      class="timeline-end pl-2 text-base-content flex flex-col"
                      @pointerdown="handleEventPointerDown($event, child.id)"
                      @pointermove="handleEventPointerMove"
                      @pointerup="handleEventPointerUp"
                      @pointercancel="handleEventPointerUp"
                      @click="handleEventItemClick(child)"
                    >
                      <time class="font-light italic text-sm">{{
                        formatPrecisionDate(child)
                      }}</time>
                      <div class="font-semibold">{{ child.title }}</div>
                      <div
                        v-if="
                          isMultiSelectMode &&
                          multiSelectStep === 'parent' &&
                          child.id === selectedParentEventId
                        "
                        class="mb-1"
                      >
                        <span class="badge badge-primary badge-sm">Parent</span>
                      </div>
                      <div class="timeline-box mt-1.5 px-3 py-2">
                        <div class="flex items-start gap-3">
                          <div
                            v-if="child.id && getEventPreviewImage(child.id)"
                            class="relative w-20 h-20 shrink-0"
                          >
                            <img
                              :src="getEventPreviewImage(child.id)"
                              :alt="`Preview for ${child.title || 'child event'}`"
                              class="w-full h-full rounded-lg object-cover"
                            />
                            <span
                              v-if="getEventImageCount(child.id) > 1"
                              class="badge badge-neutral badge-sm absolute bottom-1 right-1 bg-opacity-50"
                            >
                              + {{ getEventImageCount(child.id) - 1 }}
                            </span>
                          </div>
                          <div class="whitespace-pre-wrap text-sm opacity-80">
                            {{ child.description }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr class="bg-current" />
                  </li>
                </ul>
              </Transition>
            </div>
          </div>
        </template>

        <hr class="bg-brand-500" />
      </li>
    </ul>

    <template v-else-if="eventStore.hasActiveSearch && displayEvents.length === 0">
      <div class="h-full flex flex-col items-center justify-center pt-24">
        <div
          class="w-[min(42rem,92%)] border border-base-300 bg-base-100/90 dark:bg-base-200/70 backdrop-blur px-5 py-4 rounded-2xl shadow-sm"
        >
          <div class="text-center font-semibold">No events found for the current search</div>
          <div class="mt-1 text-center text-sm opacity-70">
            Try refining your query or use assisted search.
          </div>
          <div class="mt-3 flex justify-center">
            <button
              type="button"
              class="btn btn-primary btn-sm gap-2"
              :disabled="isAiRescueSearching || !eventStore.searchQuery.trim()"
              @click="void tryAiSearchRescue()"
            >
              <span v-if="isAiRescueSearching" class="sparkles-spinner"></span>
              <IconSparkles v-else :size="14" />
              <span>{{ isAiRescueSearching ? 'Processing...' : 'Try Assisted Search' }}</span>
            </button>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="displayEvents && displayEvents.length === 0">
      <div class="h-full flex flex-col items-center justify-center pt-24">
        <div
          class="text-brand-800 bg-brand-100 dark:bg-neutral-700 dark:text-white px-8 py-4 rounded-full w-2/3 text-center"
        >
          Looks like you haven't added any events yet. Add one below to get started!
        </div>
      </div>
    </template>

    <template v-else>
      <div class="h-full flex flex-col items-center justify-center pt-24">
        <div
          class="text-brand-800 bg-brand-100 dark:bg-neutral-700 dark:text-white px-8 py-4 rounded-full"
        >
          <div class="loader"></div>
        </div>
      </div>
    </template>
  </main>
</template>

<style scoped>
.color-iris-500 {
  color: var(--color-iris-500);
}

.sparkles-spinner {
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 999px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: timeline-search-spin 0.75s linear infinite;
}

@keyframes timeline-search-spin {
  to {
    transform: rotate(360deg);
  }
}

.child-timeline li {
  align-items: flex-start;
}

.child-timeline {
  --timeline-col-start: 0;
}

.child-timeline > li {
  --timeline-row-start: 0.5rem;
}

.child-timeline .timeline-middle {
  align-self: flex-start;
}

.child-timeline .timeline-end {
  grid-area: 1 / 3 / 4 / 4;
  place-self: start flex-start;
  margin: 0 0 0.75rem;
}

.group-toggle-btn {
  appearance: none !important;
  -webkit-appearance: none !important;
  border: 0 !important;
  border-radius: 0.5rem;
  background: transparent !important;
  background-color: transparent !important;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  box-shadow: none !important;
}

.group-toggle-btn:hover {
  background: transparent !important;
  background-color: transparent !important;
}

.group-toggle-btn:focus,
.group-toggle-btn:focus-visible,
.group-toggle-btn:active {
  outline: none !important;
  box-shadow: none !important;
  border-color: transparent !important;
  background: transparent !important;
  background-color: transparent !important;
  --tw-ring-shadow: 0 0 #0000 !important;
  --tw-ring-offset-shadow: 0 0 #0000 !important;
}

.timeline-select-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  padding: 0;
  background: transparent;
  color: inherit;
}

.timeline-select-btn.timeline-select-enabled {
  cursor: pointer;
}

.timeline-select-btn.timeline-select-enabled:hover {
  color: var(--color-brand-700);
}

.timeline-select-btn.timeline-select-active {
  color: var(--color-brand-700);
}

.timeline-select-btn.timeline-parent-active {
  color: var(--color-brand-800);
}

.timeline-select-btn:focus-visible {
  outline: 2px solid var(--color-brand-500);
  outline-offset: 2px;
}

.timeline-select-btn-sm svg {
  width: 20px;
  height: 20px;
}

.child-group-enter-active,
.child-group-leave-active {
  overflow: hidden;
  transition:
    max-height 240ms ease,
    opacity 220ms ease,
    transform 220ms ease;
}

.child-group-enter-from,
.child-group-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
}

.child-group-enter-to,
.child-group-leave-from {
  max-height: 640px;
  opacity: 1;
  transform: translateY(0);
}
</style>
